import {
    TransactionRuleActionFormSchema,
    TransactionRuleConditionFormSchema,
    FormRuleActionType,
    FormRuleConditionType,
} from "../constants";
import { CategoryVisibility } from "../useEntityCategoryVisibility";

type CategoriesById = Record<string, { isBusiness: boolean } | undefined>;

function clearCategoryAction(
    actions: TransactionRuleActionFormSchema[],
): TransactionRuleActionFormSchema[] {
    let isChanged = false;

    const nextActions = actions.map((action) => {
        if (action.actionType !== FormRuleActionType.CATEGORY) {
            return action;
        }

        if (!action.value?.categoryId) {
            return action;
        }

        isChanged = true;
        return {
            ...action,
            value: {
                ...action.value,
                categoryId: undefined,
            },
        };
    });

    return isChanged ? nextActions : actions;
}

export function clearInvalidCategoryActionForEntitySelection({
    actions,
    categoriesById,
    visibility,
}: {
    actions: TransactionRuleActionFormSchema[];
    categoriesById: CategoriesById;
    visibility: CategoryVisibility;
}): TransactionRuleActionFormSchema[] {
    const categoryAction = actions.find(
        (action) => action.actionType === FormRuleActionType.CATEGORY,
    );
    const categoryId = categoryAction?.value?.categoryId;

    if (!categoryId) {
        return actions;
    }

    const category = categoriesById[categoryId];

    if (!category) {
        return actions;
    }

    const shouldClear =
        (category.isBusiness && !visibility.showBusiness) ||
        (!category.isBusiness && !visibility.showPersonal);

    if (!shouldClear) {
        return actions;
    }

    return clearCategoryAction(actions);
}

export function clearInvalidCategoryConditionsForEntitySelection({
    conditions,
    categoriesById,
    visibility,
}: {
    conditions: TransactionRuleConditionFormSchema[];
    categoriesById: CategoriesById;
    visibility: CategoryVisibility;
}): TransactionRuleConditionFormSchema[] {
    if (visibility.showBusiness && visibility.showPersonal) {
        return conditions;
    }

    let isChanged = false;

    const nextConditions = conditions.map((condition) => {
        if (
            condition.conditionType !== FormRuleConditionType.CATEGORY &&
            condition.conditionType !== FormRuleConditionType.SUGGESTED_CATEGORY
        ) {
            return condition;
        }

        const filteredCategoryIds = condition.categoryIds?.filter(
            (categoryId) => {
                const category = categoriesById[categoryId];

                if (!category) {
                    return true;
                }

                if (!visibility.showBusiness && category.isBusiness) {
                    return false;
                }

                if (!visibility.showPersonal && !category.isBusiness) {
                    return false;
                }

                return true;
            },
        );

        if (filteredCategoryIds?.length === condition.categoryIds?.length) {
            return condition;
        }

        isChanged = true;

        return {
            ...condition,
            categoryIds:
                filteredCategoryIds && filteredCategoryIds.length > 0
                    ? filteredCategoryIds
                    : undefined,
        };
    });

    return isChanged ? nextConditions : conditions;
}
